import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Gift, Users, Megaphone, CheckCircle, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';

const PARTNER_LOGOS = [
  { name: 'Piano Love', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/7ibe6ine_e3390554-1182-4073-8dbe-9d7e0f508a62.jpeg' },
  { name: 'TheStockroom', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/ahgvaoj9_TheStockroom%20Transparent%20Logo%20%282%29.png' },
  { name: 'Save a Life Give Blood', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/i2gek46q_save-a-life-give-blood-logo-png_seeklogo-303827.webp' },
  { name: 'Wingstop', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/l913l8nb_Wingstop-Logo-500x281.webp' },
  { name: 'Red Bull Basement', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/cvewfkzl_Basement%20logo.webp' },
  { name: 'Vita Coco', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/vxejf9sq_Vita_Coco_Logo-3000x2376.webp' },
  { name: 'Islington Council', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/kunmniq4_islington-logo-news-placeholder.webp' },
  { name: 'Lifelong Links', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/jssoog2l_LL_logo.webp' },
];

const partnershipTypes = [
  {
    icon: Sparkles,
    title: 'Event Sponsorship',
    description: 'Support community events that bring people together. From family fun days to youth workshops, your brand becomes part of meaningful moments.',
    benefits: ['Brand visibility at community events', 'Social media coverage', 'Impact report with audience insights', 'Community goodwill'],
  },
  {
    icon: Users,
    title: 'Programme Partnerships',
    description: 'Co-create and support ongoing programmes that make lasting impact. Partner on youth initiatives, wellbeing projects, or community series.',
    benefits: ['Long-term community engagement', 'Detailed impact measurement', 'Co-branded content opportunities', 'CSR portfolio enhancement'],
  },
  {
    icon: Gift,
    title: 'In-Kind Support',
    description: 'Contribute products, services, or venue space to community projects. Every contribution helps us deliver more for the people we serve.',
    benefits: ['Direct community benefit', 'Flexible contribution options', 'Recognition in communications', 'Tax-efficient giving'],
  },
  {
    icon: Megaphone,
    title: 'Brand Activations with Purpose',
    description: 'Create experiences that connect your brand with communities authentically. No gimmicks, just genuine engagement with social value.',
    benefits: ['Authentic brand storytelling', 'Community-first approach', 'Memorable experiences', 'Purpose-driven marketing'],
  },
];

const whyPartner = [
  'Direct access to diverse London communities',
  'Measurable social impact with detailed reporting',
  'Authentic storytelling opportunities',
  'Flexible partnership structures',
  'Experienced project management team',
  'Community-led insights and feedback',
];

const testimonials = [
  {
    quote: "Working with OVYC has been one of the most rewarding partnerships we've undertaken. Their community-first approach meant our involvement felt genuine and impactful.",
    author: "Local Business Partner",
  },
  {
    quote: "OVYC delivered an incredible event that truly connected with our target audience. The insight report afterwards gave us real data on community engagement.",
    author: "Brand Activation Manager",
  },
];

export const PartnerPage = () => {
  return (
    <main data-testid="partner-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-4 block">Partner With Us</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#2D2A26] mb-6 font-['Manrope']">
              Create Impact That Matters
            </h1>
            <p className="text-lg md:text-xl text-[#5C5852] leading-relaxed">
              We work with brands, venues, funders and organisations to create community-led 
              experiences with real social value. Let's build something meaningful together.
            </p>
          </div>
        </div>
      </section>

      {/* Partnership Types */}
      <section 
        data-testid="partnership-types-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E76F51] mb-4 block">
              How We Partner
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              Ways to Work With Us
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {partnershipTypes.map((type, index) => (
              <div 
                key={type.title}
                data-testid={`partnership-type-${index}`}
                className="bg-[#FAF9F6] rounded-3xl p-8 border border-[#E6E2D8]/50 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#E3B505] flex items-center justify-center mb-6">
                  <type.icon className="w-7 h-7 text-[#2D2A26]" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-[#2D2A26] mb-3 font-['Manrope']">
                  {type.title}
                </h3>
                <p className="text-[#5C5852] leading-relaxed mb-6">{type.description}</p>
                <ul className="space-y-2">
                  {type.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-[#5C5852]">
                      <CheckCircle className="w-4 h-4 text-[#E3B505] flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner With OVYC */}
      <section className="py-20 md:py-28 bg-[#F4F1DE]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm font-medium tracking-wide uppercase text-[#264653] mb-4 block">
                Why OVYC
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#2D2A26] mb-6 font-['Manrope']">
                Why Partner With Us?
              </h2>
              <p className="text-[#5C5852] text-lg leading-relaxed mb-8">
                Partnering with OVYC means more than sponsorship. It means becoming part of a 
                community-led movement that delivers real results. We bring together creative 
                project management, deep community relationships, and a commitment to measurable 
                impact.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {whyPartner.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-[#E3B505] flex-shrink-0 mt-0.5" />
                    <span className="text-[#2D2A26] text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-[#264653] rounded-3xl p-8 md:p-10 text-white">
              <h3 className="text-2xl font-semibold mb-6 font-['Manrope']">Partnership Process</h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#E3B505] flex items-center justify-center flex-shrink-0 text-[#2D2A26] font-semibold text-sm">1</div>
                  <div>
                    <h4 className="font-semibold mb-1">Initial Conversation</h4>
                    <p className="text-white/70 text-sm">We learn about your goals and explore alignment opportunities.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#E3B505] flex items-center justify-center flex-shrink-0 text-[#2D2A26] font-semibold text-sm">2</div>
                  <div>
                    <h4 className="font-semibold mb-1">Partnership Proposal</h4>
                    <p className="text-white/70 text-sm">We create a tailored proposal that meets your objectives.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#E3B505] flex items-center justify-center flex-shrink-0 text-[#2D2A26] font-semibold text-sm">3</div>
                  <div>
                    <h4 className="font-semibold mb-1">Project Delivery</h4>
                    <p className="text-white/70 text-sm">We execute the partnership with full project management support.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#E3B505] flex items-center justify-center flex-shrink-0 text-[#2D2A26] font-semibold text-sm">4</div>
                  <div>
                    <h4 className="font-semibold mb-1">Impact Reporting</h4>
                    <p className="text-white/70 text-sm">You receive a detailed report on outcomes and community impact.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section 
        data-testid="testimonials-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-4 block">
              Partner Feedback
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#2D2A26] font-['Manrope']">
              What Partners Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                data-testid={`testimonial-${index}`}
                className="bg-[#FAF9F6] rounded-3xl p-8 relative"
              >
                <Quote className="w-10 h-10 text-[#E3B505]/30 absolute top-6 right-6" />
                <p className="text-lg text-[#2D2A26] leading-relaxed mb-6 relative z-10">
                  "{testimonial.quote}"
                </p>
                <p className="text-sm font-medium text-[#5C5852]">— {testimonial.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Collaborators */}
      <section 
        data-testid="collaborators-section"
        className="py-16 bg-[#FAF9F6] border-y border-[#E6E2D8]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-sm font-medium tracking-wide uppercase text-[#5C5852] mb-10">
            Organisations We've Worked With
          </p>
          <div className="flex items-center justify-center gap-8 md:gap-16 flex-wrap">
            {PARTNER_LOGOS.map((partner) => (
              <img 
                key={partner.name}
                src={partner.src}
                alt={partner.name}
                className="h-12 md:h-16 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#E3B505]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] mb-6 font-['Manrope']">
            Ready to Start a Conversation?
          </h2>
          <p className="text-lg md:text-xl text-[#2D2A26]/80 mb-10 max-w-2xl mx-auto">
            We'd love to hear about your organisation and explore how we can create meaningful 
            impact together. Get in touch to start the conversation.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="partner-cta-btn"
              className="bg-[#264653] text-white hover:bg-[#264653]/90 rounded-full h-14 px-10 text-base font-medium shadow-lg"
            >
              Contact Us Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default PartnerPage;
